<template>
  <div
    class="api-key"
    :class="{ 'api-key--expanded': expanded }"
  >
    <div class="api-key__header">
      <IconButton
        size="lg"
        flair="soft"
        kind="transparent"
        @click="toggleExpanded"
      >
        <Icon :name="expanded ? 'fill-small-chevron-top' : 'fill-small-chevron-down'" />
      </IconButton>

      <ToolTip
        :content="apiKey.name"
        :delay="[1000, 0]"
        class="api-key__header__name"
      >
        <div>
          {{ apiKey.name }}
        </div>
      </ToolTip>

      <div class="api-key__header__icon">
        <slot name="icon" />
      </div>

      <div class="api-key__header__prefix">
        Starts with
        <span class="api-key__header__prefix__span">{{ apiKey.prefix }}</span>
      </div>

      <slot><KeyListActions :api-key="apiKey" /></slot>
    </div>

    <Divider v-if="!expanded" />

    <transition name="toggle">
      <div
        v-if="expanded"
        class="api-key__permissions"
      >
        <div
          v-for="(group, index) in nonEmptyGroups"
          :key="group.id"
        >
          <PermissionGroup
            class="api-key__permissions__group"
            :group="group"
          />
          <Divider
            v-if="index !== nonEmptyGroups.length - 1"
            class="divider"
          />
        </div>

        <Divider
          v-if="modelSpecificPermissions.length > 0"
          class="divider"
        />
        <ModelPermissionList
          v-if="modelSpecificPermissions.length > 0"
          class="api-key__permissions__group"
          :permissions="modelSpecificPermissions"
        />
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
export default {
  name: 'KeyListItem',
}
</script>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import IconButton from '@/uiKit/Button/IconButton.vue'
import Icon from '@/uiKit/Icon/Icon.vue'
import Divider from '@/uiKit/Divider/Divider.vue'

import { PERMISSION_GROUPS, GROUPED_ABILITIES } from './data'
import ModelPermissionList from './ModelPermissionList.vue'
import PermissionGroup from './PermissionGroup.vue'
import KeyListActions from './KeyListActions.vue'
import type { ApiKeyPayload, ApiKeyPermission, Group } from './types'
import ToolTip from '@/uiKit/ToolTip/ToolTip.vue'

/**
 * Generalized component used to render an api key in a list.
 *
 * Provides two slots to render additional UI.
 *
 * The default slot is rendered all the way to the right and is intended for
 * action buttons.
 *
 * The `icon` slot is rendered all the way to the left and is intended for icons
 * depicting key associations (team or user).
 *
 * The component toggles between expanded and collapsed states.
 *
 * The expanded state shows permissions associated with the key.
 */

const props = defineProps<{ apiKey: ApiKeyPayload }>()

const expanded = ref(false)

const toggleExpanded = (): void => {
  expanded.value = !expanded.value
}

const generalPermissions = computed(() => props.apiKey.permissions.filter((p) => p[1] === 'all'))

const nonEmptyGroups = computed<(Group & { permissions: ApiKeyPermission[] })[]>(() =>
  PERMISSION_GROUPS.map((g) => ({
    id: g.id,
    name: g.name,
    permissions: generalPermissions.value.filter((p) => GROUPED_ABILITIES[g.id].includes(p[0])),
  })).filter((g) => g.permissions.length > 0),
)

const modelSpecificPermissions = computed<ApiKeyPermission[]>(() =>
  props.apiKey.permissions.filter((p) => p[1].indexOf('trained_model:') === 0),
)
</script>

<style lang="scss" scoped>
.api-key {
  @include col;
  border-radius: 16px;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }

  &--expanded {
    background-color: var(--color-surface-secondary);
  }
}

.api-key__header {
  @include row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 8px;
  gap: 8px;
}

.api-key__header__name {
  @include ellipsis;
  @include typographyRegularBody200;
  flex: 1;
  color: var(--color-text-subtle);
}

.api-key__header__icon {
  flex: 1;
  display: flex;
  align-items: flex-start;
}

.api-key__header__prefix {
  padding: 8px 12px;
  @include typographyRegularBody200;
  color: var(--color-text-subtle);
  background-color: var(--color-surface-secondary);
  border-radius: 6px;
}

.api-key__header__prefix__span {
  @include typography(md-2, source);
  margin-left: 4px;
  color: var(--color-text-default);
}

.api-key__permissions {
  margin: 0 8px 8px;
  padding: 16px;
  border-radius: 10px;
  background-color: var(--color-surface-default);

  @include col;
}

.divider {
  margin: 8px 0;
}
</style>
